import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { padStart, uniq, find } from 'lodash-es';
import { getShooters, getScores, getStages, getMatchStaff } from '../../api';
import { summarizeScore, getLatestScoreForShooterPerStage } from '../../utils/scoring';
import WINMSS_COUNTRIES from './winmsscountries.json';

function xmlEscape(str) {
  return String(str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

function publicIdToRegion(publicId) {
  if (publicId.indexOf('|') === -1) return 'ISR';
  const countryCode = publicId.split('|')[0];
  return countryCodeToRegion(countryCode);
}

function countryCodeToRegion(cc) {
  const entry = WINMSS_COUNTRIES.find((a) => a.iso3166_2 === cc);
  if (!entry && cc === 'ME') return 'MTO';
  if (!entry && cc === 'FR') return 'FRA';
  if (!entry && cc === 'RS') return 'SER';
  if (!entry) return 'ISR';
  return entry.winmss;
}

function saveData(text, filename) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function getMemberData({ shooters }) {
  return `${''
    + '<xml xmlns:s=\'uuid:BDC6E3F0-6DA3-11d1-A2A3-00AA00C14882\'\n'
    + '\txmlns:dt=\'uuid:C2F41010-65B3-11d1-A29F-00AA00C14882\'\n'
    + '\txmlns:rs=\'urn:schemas-microsoft-com:rowset\'\n'
    + '\txmlns:z=\'#RowsetSchema\'>\n'
    + '<s:Schema id=\'RowsetSchema\'>\n'
    + '\t<s:ElementType name=\'row\' content=\'eltOnly\' rs:updatable=\'true\'>\n'
    + '\t\t<s:AttributeType name=\'MemberId\' rs:number=\'1\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMember\'\n'
    + '\t\t\t rs:basecolumn=\'MemberId\' rs:keycolumn=\'true\' rs:autoincrement=\'true\'>\n'
    + '\t\t\t<s:datatype dt:type=\'int\' dt:maxLength=\'4\' rs:precision=\'10\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Lastname\' rs:number=\'2\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMember\' rs:basecolumn=\'Lastname\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'25\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Firstname\' rs:number=\'3\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMember\' rs:basecolumn=\'Firstname\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'15\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Init\' rs:number=\'4\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMember\'\n'
    + '\t\t\t rs:basecolumn=\'Comment\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'1\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'IcsAlias\' rs:number=\'5\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMember\' rs:basecolumn=\'ICS\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'16\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'RefNo\' rs:number=\'6\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMember\'\n'
    + '\t\t\t rs:basecolumn=\'RefNo\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'10\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'InActive\' rs:number=\'7\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMember\'\n'
    + '\t\t\t rs:basecolumn=\'InActive\'>\n'
    + '\t\t\t<s:datatype dt:type=\'boolean\' dt:maxLength=\'2\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Female\' rs:number=\'8\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMember\'\n'
    + '\t\t\t rs:basecolumn=\'Female\'>\n'
    + '\t\t\t<s:datatype dt:type=\'boolean\' dt:maxLength=\'2\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'PrintLabel\' rs:number=\'9\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMember\'\n'
    + '\t\t\t rs:basecolumn=\'PrintLabel\'>\n'
    + '\t\t\t<s:datatype dt:type=\'boolean\' dt:maxLength=\'2\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'RegionId\' rs:number=\'10\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMember\' rs:basecolumn=\'TypeRegionId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'3\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'DOB\' rs:number=\'11\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMember\'\n'
    + '\t\t\t rs:basecolumn=\'DOB\'>\n'
    + '\t\t\t<s:datatype dt:type=\'dateTime\' rs:dbtype=\'variantdate\' dt:maxLength=\'16\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Address1\' rs:number=\'12\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMember\' rs:basecolumn=\'Address1\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'40\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Address2\' rs:number=\'13\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMember\' rs:basecolumn=\'Address2\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'40\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'City\' rs:number=\'14\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMember\'\n'
    + '\t\t\t rs:basecolumn=\'City\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'20\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Province\' rs:number=\'15\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMember\' rs:basecolumn=\'Province\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'24\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'CountryId\' rs:number=\'16\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMember\' rs:basecolumn=\'TypeCountryId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'3\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'PostCode\' rs:number=\'17\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMember\' rs:basecolumn=\'PostCode\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'10\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'PhoneHome\' rs:number=\'18\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMember\' rs:basecolumn=\'PhoneHome\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'20\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'PhoneAlt\' rs:number=\'19\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMember\' rs:basecolumn=\'PhoneAlternate\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'20\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'PhoneFax\' rs:number=\'20\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMember\' rs:basecolumn=\'PhoneFax\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'20\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Email\' rs:number=\'21\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMember\'\n'
    + '\t\t\t rs:basecolumn=\'Email\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'36\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'ClassId\' rs:number=\'22\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMember\' rs:basecolumn=\'TypeScoreClassId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'1\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'DfltDivId\' rs:number=\'23\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMember\' rs:basecolumn=\'DfltDivisionId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'DfltCatId\' rs:number=\'24\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMember\' rs:basecolumn=\'DfltNonTeamCategoryId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'DfltTagId\' rs:number=\'25\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMember\' rs:basecolumn=\'DfltTagId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'int\' dt:maxLength=\'4\' rs:precision=\'10\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'QualId\' rs:number=\'26\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMember\' rs:basecolumn=\'TypeQualificationId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'int\' dt:maxLength=\'4\' rs:precision=\'10\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Register\' rs:number=\'27\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMember\'\n'
    + '\t\t\t rs:basecolumn=\'Register\'>\n'
    + '\t\t\t<s:datatype dt:type=\'boolean\' dt:maxLength=\'2\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Note\' rs:number=\'28\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMember\'\n'
    + '\t\t\t rs:basecolumn=\'Note\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'536870910\' rs:long=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:extends type=\'rs:rowbase\'/>\n'
    + '\t</s:ElementType>\n'
    + '</s:Schema>\n'
    + '<rs:data>\n'}${
    shooters.map((shooter, shooterIdx) => {
      if (shooter.cancelledParticipation) return '';
      const firstName = (shooter.name || '').trim().split(' ')[0].replace(/'/g, '&apos;');
      const lastName = (shooter.name || '').trim().substr(firstName.length + 1).replace(/'/g, '&apos;');

      return `   <z:row MemberId='${shooterIdx + 1}' Lastname='${lastName || ' '}' Firstname='${firstName}' IcsAlias='${shooter.publicId}' RefNo='' InActive='False' Female='${shooter.category === 'lady' ? 'TRUE' : 'FALSE'}'`
        + `\t    PrintLabel='False' RegionId='${publicIdToRegion(shooter.publicId)}' CountryId='' ClassId='U' DfltDivId='2' DfltCatId='0' DfltTagId='0' QualId='0'`
        + '\t    Register=\'False\'/>\n';
    }).join('')
  }</rs:data>\n`
    + '</xml>\n';
}

function getMatchData({ match, staff, isPcc }) {
  const md = ((staff || []).find((s) => s.role === 'md') || {}).name || 'MD';
  const rm = ((staff || []).find((s) => s.role === 'rm') || {}).name || 'RM';
  const so = ((staff || []).find((s) => s.role === 'so') || {}).name || 'SO';
  return ''
    + '<xml xmlns:s=\'uuid:BDC6E3F0-6DA3-11d1-A2A3-00AA00C14882\'\n'
    + '\txmlns:dt=\'uuid:C2F41010-65B3-11d1-A29F-00AA00C14882\'\n'
    + '\txmlns:rs=\'urn:schemas-microsoft-com:rowset\'\n'
    + '\txmlns:z=\'#RowsetSchema\'>\n'
    + '<s:Schema id=\'RowsetSchema\'>\n'
    + '\t<s:ElementType name=\'row\' content=\'eltOnly\' rs:updatable=\'true\'>\n'
    + '\t\t<s:AttributeType name=\'MatchId\' rs:number=\'1\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatch\'\n'
    + '\t\t\t rs:basecolumn=\'MatchId\' rs:keycolumn=\'true\' rs:autoincrement=\'true\'>\n'
    + '\t\t\t<s:datatype dt:type=\'int\' dt:maxLength=\'4\' rs:precision=\'10\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'MatchName\' rs:number=\'2\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatch\' rs:basecolumn=\'MatchName\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'50\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'MatchDt\' rs:number=\'3\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatch\' rs:basecolumn=\'MatchDt\'>\n'
    + '\t\t\t<s:datatype dt:type=\'dateTime\' rs:dbtype=\'variantdate\' dt:maxLength=\'16\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'ClubId\' rs:number=\'4\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatch\'\n'
    + '\t\t\t rs:basecolumn=\'ClubId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'i2\' dt:maxLength=\'2\' rs:precision=\'5\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Chrono\' rs:number=\'5\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatch\'\n'
    + '\t\t\t rs:basecolumn=\'Chronograph\'>\n'
    + '\t\t\t<s:datatype dt:type=\'boolean\' dt:maxLength=\'2\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'MatchLevel\' rs:number=\'6\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatch\' rs:basecolumn=\'TypeMatchLevel\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'CountryId\' rs:number=\'7\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatch\' rs:basecolumn=\'TypeCountryId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'3\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'FirearmId\' rs:number=\'8\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatch\' rs:basecolumn=\'TypeFirearmId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'SquadCount\' rs:number=\'9\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatch\' rs:basecolumn=\'SquadCount\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'MD\' rs:number=\'10\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatch\'\n'
    + '\t\t\t rs:basecolumn=\'MatchDirector\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'40\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'RM\' rs:number=\'11\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatch\'\n'
    + '\t\t\t rs:basecolumn=\'RangeMaster\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'40\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'SD\' rs:number=\'12\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatch\'\n'
    + '\t\t\t rs:basecolumn=\'StatsDirector\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'40\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:extends type=\'rs:rowbase\'/>\n'
    + '\t</s:ElementType>\n'
    + '</s:Schema>\n'
    + '<rs:data>\n'
    + `\t<z:row MatchId='1' MatchName='${match.title}${isPcc ? ' (PCC)' : ''}' MatchDt='${match.startDate.year}-${padStart(String(match.startDate.month), 2, '0')}-${padStart(String(match.startDate.day), 2, '0')}T00:00:00' ClubId='1' Chrono='False' MatchLevel='2' CountryId='${countryCodeToRegion(match.region)}'\n`
    + `\t\t FirearmId='${isPcc ? 7 : 1}' SquadCount='${match.squads.length}' MD='${md}' RM='${rm}' SD='${so}'/>\n`
    + '</rs:data>\n'
    + '</xml>\n';
}

function getSquadData({ match }) {
  return `${''
    + '<xml xmlns:s=\'uuid:BDC6E3F0-6DA3-11d1-A2A3-00AA00C14882\'\n'
    + '\txmlns:dt=\'uuid:C2F41010-65B3-11d1-A29F-00AA00C14882\'\n'
    + '\txmlns:rs=\'urn:schemas-microsoft-com:rowset\'\n'
    + '\txmlns:z=\'#RowsetSchema\'>\n'
    + '<s:Schema id=\'RowsetSchema\'>\n'
    + '\t<s:ElementType name=\'row\' content=\'eltOnly\' rs:updatable=\'true\'>\n'
    + '\t\t<s:AttributeType name=\'MatchId\' rs:number=\'1\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchSquad\' rs:basecolumn=\'MatchId\' rs:keycolumn=\'true\'>\n'
    + '\t\t\t<s:datatype dt:type=\'int\' dt:maxLength=\'4\' rs:precision=\'10\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'SquadId\' rs:number=\'2\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchSquad\' rs:basecolumn=\'SquadId\' rs:keycolumn=\'true\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Squad\' rs:number=\'3\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchSquad\'\n'
    + '\t\t\t rs:basecolumn=\'Squad\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'3\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:extends type=\'rs:rowbase\'/>\n'
    + '\t</s:ElementType>\n'
    + '</s:Schema>\n'
    + '<rs:data>\n'}${
    match.squads.map((squad, squadIdx) => `\t<z:row MatchId='1' SquadId='${squadIdx + 1}' Squad='${squad.title}'/>\n`).join('')
  }</rs:data>\n`
    + '</xml>\n';
}

function getStagesData({ stages }) {
  return `${''
    + '<xml xmlns:s=\'uuid:BDC6E3F0-6DA3-11d1-A2A3-00AA00C14882\'\n'
    + '\txmlns:dt=\'uuid:C2F41010-65B3-11d1-A29F-00AA00C14882\'\n'
    + '\txmlns:rs=\'urn:schemas-microsoft-com:rowset\'\n'
    + '\txmlns:z=\'#RowsetSchema\'>\n'
    + '<s:Schema id=\'RowsetSchema\'>\n'
    + '\t<s:ElementType name=\'row\' content=\'eltOnly\' rs:updatable=\'true\'>\n'
    + '\t\t<s:AttributeType name=\'MatchId\' rs:number=\'1\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStage\' rs:basecolumn=\'MatchId\' rs:keycolumn=\'true\'>\n'
    + '\t\t\t<s:datatype dt:type=\'int\' dt:maxLength=\'4\' rs:precision=\'10\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'StageId\' rs:number=\'2\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStage\' rs:basecolumn=\'StageId\' rs:keycolumn=\'true\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'StageName\' rs:number=\'3\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStage\' rs:basecolumn=\'StageName\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'32\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Location\' rs:number=\'4\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStage\' rs:basecolumn=\'Location\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'32\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'FirearmId\' rs:number=\'5\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStage\' rs:basecolumn=\'TypeFirearmId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'CourseId\' rs:number=\'6\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStage\' rs:basecolumn=\'TypeStageCourseId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'ScoringId\' rs:number=\'7\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStage\' rs:basecolumn=\'TypeScoringId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'TrgtTypeId\' rs:number=\'8\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStage\' rs:basecolumn=\'TypeTargetClassifyId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'IcsStageId\' rs:number=\'9\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStage\' rs:basecolumn=\'TypeStdStageSetupId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'i2\' dt:maxLength=\'2\' rs:precision=\'5\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Remove\' rs:number=\'10\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchStage\'\n'
    + '\t\t\t rs:basecolumn=\'RemoveScoring\'>\n'
    + '\t\t\t<s:datatype dt:type=\'boolean\' dt:maxLength=\'2\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'TrgtPaper\' rs:number=\'11\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStage\' rs:basecolumn=\'TrgtPaper\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'TrgtPopper\' rs:number=\'12\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStage\' rs:basecolumn=\'TrgtPopper\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'TrgtPlates\' rs:number=\'13\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStage\' rs:basecolumn=\'TrgtPlates\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'TrgtVanish\' rs:number=\'14\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStage\' rs:basecolumn=\'TrgtDisappear\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'TrgtPenlty\' rs:number=\'15\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStage\' rs:basecolumn=\'TrgtPenalty\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'MinRounds\' rs:number=\'16\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStage\' rs:basecolumn=\'MinRounds\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'ReportOn\' rs:number=\'17\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchStage\'\n'
    + '\t\t\t rs:basecolumn=\'ReportOn\'>\n'
    + '\t\t\t<s:datatype dt:type=\'boolean\' dt:maxLength=\'2\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'MaxPoints\' rs:number=\'18\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStage\' rs:basecolumn=\'MaxPoints\'>\n'
    + '\t\t\t<s:datatype dt:type=\'i2\' dt:maxLength=\'2\' rs:precision=\'5\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'StartPos\' rs:number=\'19\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStage\' rs:basecolumn=\'StartPosition\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'536870910\' rs:long=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'StartOn\' rs:number=\'20\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStage\' rs:basecolumn=\'StartOn\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'8\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'StringCnt\' rs:number=\'21\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStage\' rs:basecolumn=\'StringsOfFire\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Descriptn\' rs:number=\'22\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStage\' rs:basecolumn=\'Description\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'536870910\' rs:long=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:extends type=\'rs:rowbase\'/>\n'
    + '\t</s:ElementType>\n'
    + '</s:Schema>\n'
    + '<rs:data>\n'}${
    stages.map((stage, stageIdx) => ''
      + `<z:row MatchId='1' StageId='${stageIdx + 1}' StageName='${stage.title}' Location='1' FirearmId='1' CourseId='3' ScoringId='1' TrgtTypeId='2'`
      + `     IcsStageId='0' Remove='False' TrgtPaper='${stage.targets?.paper || 0}' TrgtPopper='${stage.targets?.poppers || 0}' TrgtPlates='${stage.targets?.plates || 0}' TrgtVanish='0' TrgtPenlty='0' MinRounds='${stage.rounds}'`
      + `     ReportOn='False' MaxPoints='${stage.rounds * 5}' StartPos=''`
      + '     StartOn=\'00\' StringCnt=\'0\' Descriptn=\'\'/>\n').join('')
  }</rs:data>\n`
    + '</xml>\n';
}

function getClassifyData() {
  return ''
    + '<xml xmlns:s=\'uuid:BDC6E3F0-6DA3-11d1-A2A3-00AA00C14882\'\n'
    + '\txmlns:dt=\'uuid:C2F41010-65B3-11d1-A29F-00AA00C14882\'\n'
    + '\txmlns:rs=\'urn:schemas-microsoft-com:rowset\'\n'
    + '\txmlns:z=\'#RowsetSchema\'>\n'
    + '<s:Schema id=\'RowsetSchema\'>\n'
    + '\t<s:ElementType name=\'row\' content=\'eltOnly\' rs:updatable=\'true\'>\n'
    + '\t\t<s:AttributeType name=\'MemberId\' rs:number=\'1\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMemberClassify\'\n'
    + '\t\t\t rs:basecolumn=\'MemberId\' rs:keycolumn=\'true\'>\n'
    + '\t\t\t<s:datatype dt:type=\'int\' dt:maxLength=\'4\' rs:precision=\'10\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'DivId\' rs:number=\'2\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMemberClassify\'\n'
    + '\t\t\t rs:basecolumn=\'TypeDivisionId\' rs:keycolumn=\'true\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'IntlId\' rs:number=\'3\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMemberClassify\'\n'
    + '\t\t\t rs:basecolumn=\'TypeClassIntlId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'1\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'NatlId\' rs:number=\'4\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMemberClassify\'\n'
    + '\t\t\t rs:basecolumn=\'TypeClassNatlId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'1\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'ClassId\' rs:number=\'5\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMemberClassify\' rs:basecolumn=\'TypeScoreClassId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'1\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:extends type=\'rs:rowbase\'/>\n'
    + '\t</s:ElementType>\n'
    + '</s:Schema>\n'
    + '<rs:data>\n'
    + '</rs:data>\n'
    + '</xml>\n';
}

function getClubsData({ clubs }) {
  return `${''
    + '<xml xmlns:s=\'uuid:BDC6E3F0-6DA3-11d1-A2A3-00AA00C14882\'\n'
    + '\txmlns:dt=\'uuid:C2F41010-65B3-11d1-A29F-00AA00C14882\'\n'
    + '\txmlns:rs=\'urn:schemas-microsoft-com:rowset\'\n'
    + '\txmlns:z=\'#RowsetSchema\'>\n'
    + '<s:Schema id=\'RowsetSchema\'>\n'
    + '\t<s:ElementType name=\'row\' content=\'eltOnly\' rs:updatable=\'true\'>\n'
    + '\t\t<s:AttributeType name=\'ClubId\' rs:number=\'1\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatch\'\n'
    + '\t\t\t rs:basecolumn=\'ClubId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'i2\' dt:maxLength=\'2\' rs:precision=\'5\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'ClubCode\' rs:number=\'2\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblClub\' rs:basecolumn=\'ClubCode\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'6\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Club\' rs:number=\'3\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblClub\'\n'
    + '\t\t\t rs:basecolumn=\'Club\' rs:keycolumn=\'true\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'50\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Contact\' rs:number=\'4\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblClub\' rs:basecolumn=\'Contact\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'50\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Address1\' rs:number=\'5\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblClub\' rs:basecolumn=\'Address1\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'40\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Address2\' rs:number=\'6\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblClub\' rs:basecolumn=\'Address2\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'40\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'City\' rs:number=\'7\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblClub\'\n'
    + '\t\t\t rs:basecolumn=\'City\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'20\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Province\' rs:number=\'8\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblClub\' rs:basecolumn=\'Province\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'24\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'CountryId\' rs:number=\'9\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblClub\' rs:basecolumn=\'TypeCountryId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'3\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'PostCode\' rs:number=\'10\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblClub\' rs:basecolumn=\'PostCode\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'10\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Phone\' rs:number=\'11\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblClub\'\n'
    + '\t\t\t rs:basecolumn=\'Phone\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'20\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'PhoneAlt\' rs:number=\'12\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblClub\' rs:basecolumn=\'PhoneAlternate\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'20\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'PhoneFax\' rs:number=\'13\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblClub\' rs:basecolumn=\'PhoneFax\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'20\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Email\' rs:number=\'14\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblClub\'\n'
    + '\t\t\t rs:basecolumn=\'Email\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'36\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'WebSite\' rs:number=\'15\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblClub\' rs:basecolumn=\'WebSite\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'64\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:extends type=\'rs:rowbase\'/>\n'
    + '\t</s:ElementType>\n'
    + '</s:Schema>\n'
    + '<rs:data>\n'}${
    clubs.map((club, clubIdx) => `\t<z:row ClubId='${clubIdx + 1}' Club='${xmlEscape(club)}'/>\n`).join('')
  }</rs:data>\n`
    + '</xml>\n';
}

function getEnrolledData({ shooters, scores, match }) {
  return `${''
    + '<xml xmlns:s=\'uuid:BDC6E3F0-6DA3-11d1-A2A3-00AA00C14882\'\n'
    + '\txmlns:dt=\'uuid:C2F41010-65B3-11d1-A29F-00AA00C14882\'\n'
    + '\txmlns:rs=\'urn:schemas-microsoft-com:rowset\'\n'
    + '\txmlns:z=\'#RowsetSchema\'>\n'
    + '<s:Schema id=\'RowsetSchema\'>\n'
    + '\t<s:ElementType name=\'row\' content=\'eltOnly\' rs:updatable=\'true\'>\n'
    + '\t\t<s:AttributeType name=\'MatchId\' rs:number=\'1\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchCompetitor\' rs:basecolumn=\'MatchId\' rs:keycolumn=\'true\'>\n'
    + '\t\t\t<s:datatype dt:type=\'int\' dt:maxLength=\'4\' rs:precision=\'10\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'MemberId\' rs:number=\'2\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchCompetitor\' rs:basecolumn=\'MemberId\' rs:keycolumn=\'true\'>\n'
    + '\t\t\t<s:datatype dt:type=\'int\' dt:maxLength=\'4\' rs:precision=\'10\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'CompId\' rs:number=\'3\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchCompetitor\'\n'
    + '\t\t\t rs:basecolumn=\'CompetitorId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'i2\' dt:maxLength=\'2\' rs:precision=\'5\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'DivId\' rs:number=\'4\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchCompetitor\'\n'
    + '\t\t\t rs:basecolumn=\'TypeDivisionId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'CatId\' rs:number=\'5\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchCompetitor\'\n'
    + '\t\t\t rs:basecolumn=\'TypeNonTeamCategoryId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'TeamId\' rs:number=\'6\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchCompetitor\'\n'
    + '\t\t\t rs:basecolumn=\'TeamId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'i2\' dt:maxLength=\'2\' rs:precision=\'5\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'SquadId\' rs:number=\'7\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchCompetitor\' rs:basecolumn=\'SquadId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'TagId\' rs:number=\'8\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchCompetitor\'\n'
    + '\t\t\t rs:basecolumn=\'TagId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'int\' dt:maxLength=\'4\' rs:precision=\'10\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'MajorPF\' rs:number=\'9\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchCompetitor\'\n'
    + '\t\t\t rs:basecolumn=\'MajorPowerFactor\'>\n'
    + '\t\t\t<s:datatype dt:type=\'boolean\' dt:maxLength=\'2\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Classified\' rs:number=\'10\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchCompetitor\'\n'
    + '\t\t\t rs:basecolumn=\'ClassifiedScoring\'>\n'
    + '\t\t\t<s:datatype dt:type=\'boolean\' dt:maxLength=\'2\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'IsDisq\' rs:number=\'11\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchCompetitor\'\n'
    + '\t\t\t rs:basecolumn=\'IsDisqualified\'>\n'
    + '\t\t\t<s:datatype dt:type=\'boolean\' dt:maxLength=\'2\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'DisqRuleId\' rs:number=\'12\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchCompetitor\' rs:basecolumn=\'TypeDisqualifyRuleId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'i2\' dt:maxLength=\'2\' rs:precision=\'5\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'StageDisq\' rs:number=\'13\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchCompetitor\'\n'
    + '\t\t\t rs:basecolumn=\'StageDisqualification\'>\n'
    + '\t\t\t<s:datatype dt:type=\'boolean\' dt:maxLength=\'2\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'DisqDt\' rs:number=\'14\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchCompetitor\' rs:basecolumn=\'DisqualifyDateTime\'>\n'
    + '\t\t\t<s:datatype dt:type=\'dateTime\' rs:dbtype=\'variantdate\' dt:maxLength=\'16\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'DisqMemo\' rs:number=\'15\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchCompetitor\' rs:basecolumn=\'DisqualifyMemo\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'536870910\' rs:long=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'RefNo\' rs:number=\'16\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchCompetitor\'\n'
    + '\t\t\t rs:basecolumn=\'RefNo\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'10\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:extends type=\'rs:rowbase\'/>\n'
    + '\t</s:ElementType>\n'
    + '</s:Schema>\n'
    + '<rs:data>\n'}${
    shooters.map((shooter, shooterIdx) => {
      if (shooter.cancelledParticipation) return '';
      const squadIdx = match.squads.findIndex((sq) => sq.id === shooter.squad);
      const division = {
        open: 1,
        standard: 2,
        production: 4,
        revolver: 5,
        classic: 18,
        'production-optics': 24,
        'production-optics-light': 28,
        'pcc-optics': 29,
        'pcc-iron': 30,
        optics: 50,
      }[shooter.division];
      const category = {
        lady: 1,
        junior: 2,
        senior: 3,
        'super-senior': 4,
        'grand-senior': 6,
        'lady-senior': 7,
        'super-junior': 5,
      }[shooter.category] || 0;

      const dq = find(scores.map((scoresForStage) => scoresForStage.find((score) => score.dq && score.shooterId === shooter.id)), (a) => a);
      return ''
        + `\t<z:row MatchId='1' MemberId='${shooterIdx + 1}' CompId='${shooterIdx + 1}' DivId='${division}' CatId='${category}' SquadId='${squadIdx + 1}' TagId='0' MajorPF='${shooter['power-factor'] === 'major' ? 'TRUE' : 'FALSE'}' Classified='False'\n`
        + `\t\t IsDisq='${dq ? 'True' : 'False'}' DisqRuleId='${dq ? '1' : '0'}' StageDisq='False' RefNo='UUU'/>\n`;
    }).join('')
  }</rs:data>\n`
    + '</xml>\n';
}

function getTagData() {
  return ''
    + '<xml xmlns:s=\'uuid:BDC6E3F0-6DA3-11d1-A2A3-00AA00C14882\'\n'
    + '\txmlns:dt=\'uuid:C2F41010-65B3-11d1-A29F-00AA00C14882\'\n'
    + '\txmlns:rs=\'urn:schemas-microsoft-com:rowset\'\n'
    + '\txmlns:z=\'#RowsetSchema\'>\n'
    + '<s:Schema id=\'RowsetSchema\'>\n'
    + '\t<s:ElementType name=\'row\' content=\'eltOnly\' rs:updatable=\'true\'>\n'
    + '\t\t<s:AttributeType name=\'TagId\' rs:number=\'1\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblTag\'\n'
    + '\t\t\t rs:basecolumn=\'TagId\' rs:keycolumn=\'true\' rs:autoincrement=\'true\'>\n'
    + '\t\t\t<s:datatype dt:type=\'int\' dt:maxLength=\'4\' rs:precision=\'10\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Tag\' rs:number=\'2\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblTag\'\n'
    + '\t\t\t rs:basecolumn=\'Tag\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'3\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:extends type=\'rs:rowbase\'/>\n'
    + '\t</s:ElementType>\n'
    + '</s:Schema>\n'
    + '<rs:data>\n'
    + '</rs:data>\n'
    + '</xml>';
}

function getTeamData() {
  return ''
    + '<xml xmlns:s=\'uuid:BDC6E3F0-6DA3-11d1-A2A3-00AA00C14882\'\n'
    + '\txmlns:dt=\'uuid:C2F41010-65B3-11d1-A29F-00AA00C14882\'\n'
    + '\txmlns:rs=\'urn:schemas-microsoft-com:rowset\'\n'
    + '\txmlns:z=\'#RowsetSchema\'>\n'
    + '<s:Schema id=\'RowsetSchema\'>\n'
    + '\t<s:ElementType name=\'row\' content=\'eltOnly\' rs:updatable=\'true\'>\n'
    + '\t\t<s:AttributeType name=\'MatchId\' rs:number=\'1\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchTeam\' rs:basecolumn=\'MatchId\' rs:keycolumn=\'true\'>\n'
    + '\t\t\t<s:datatype dt:type=\'int\' dt:maxLength=\'4\' rs:precision=\'10\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'TeamId\' rs:number=\'2\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchTeam\'\n'
    + '\t\t\t rs:basecolumn=\'TeamId\' rs:keycolumn=\'true\'>\n'
    + '\t\t\t<s:datatype dt:type=\'i2\' dt:maxLength=\'2\' rs:precision=\'5\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'DivId\' rs:number=\'3\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchTeam\'\n'
    + '\t\t\t rs:basecolumn=\'TypeDivisionId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'CatId\' rs:number=\'4\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchTeam\'\n'
    + '\t\t\t rs:basecolumn=\'TypeNonTeamCategoryId\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Team\' rs:number=\'5\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchTeam\'\n'
    + '\t\t\t rs:basecolumn=\'Team\'>\n'
    + '\t\t\t<s:datatype dt:type=\'string\' dt:maxLength=\'32\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:extends type=\'rs:rowbase\'/>\n'
    + '\t</s:ElementType>\n'
    + '</s:Schema>\n'
    + '<rs:data>\n'
    + '</rs:data>\n'
    + '</xml>';
}

function getScoreData({ shooters, scores, stages }) {
  return `${''
    + '<xml xmlns:s=\'uuid:BDC6E3F0-6DA3-11d1-A2A3-00AA00C14882\'\n'
    + '\txmlns:dt=\'uuid:C2F41010-65B3-11d1-A29F-00AA00C14882\'\n'
    + '\txmlns:rs=\'urn:schemas-microsoft-com:rowset\'\n'
    + '\txmlns:z=\'#RowsetSchema\'>\n'
    + '<s:Schema id=\'RowsetSchema\'>\n'
    + '\t<s:ElementType name=\'row\' content=\'eltOnly\' rs:updatable=\'true\'>\n'
    + '\t\t<s:AttributeType name=\'MatchId\' rs:number=\'1\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStageScore\' rs:basecolumn=\'MatchId\' rs:keycolumn=\'true\'>\n'
    + '\t\t\t<s:datatype dt:type=\'int\' dt:maxLength=\'4\' rs:precision=\'10\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'StageId\' rs:number=\'2\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStageScore\' rs:basecolumn=\'StageId\' rs:keycolumn=\'true\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'MemberId\' rs:number=\'3\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStageScore\' rs:basecolumn=\'MemberId\' rs:keycolumn=\'true\'>\n'
    + '\t\t\t<s:datatype dt:type=\'int\' dt:maxLength=\'4\' rs:precision=\'10\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'ScoreA\' rs:number=\'4\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchStageScore\'\n'
    + '\t\t\t rs:basecolumn=\'ScoreA\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'ScoreB\' rs:number=\'5\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchStageScore\'\n'
    + '\t\t\t rs:basecolumn=\'ScoreB\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'ScoreC\' rs:number=\'6\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchStageScore\'\n'
    + '\t\t\t rs:basecolumn=\'ScoreC\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'ScoreD\' rs:number=\'7\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchStageScore\'\n'
    + '\t\t\t rs:basecolumn=\'ScoreD\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Misses\' rs:number=\'8\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchStageScore\'\n'
    + '\t\t\t rs:basecolumn=\'Misses\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Penalties\' rs:number=\'9\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStageScore\' rs:basecolumn=\'Penalties\'>\n'
    + '\t\t\t<s:datatype dt:type=\'i2\' dt:maxLength=\'2\' rs:precision=\'5\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'ProcError\' rs:number=\'10\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStageScore\' rs:basecolumn=\'Procedurals\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'ShootTime\' rs:number=\'11\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStageScore\' rs:basecolumn=\'Time\'>\n'
    + '\t\t\t<s:datatype dt:type=\'r4\' dt:maxLength=\'4\' rs:precision=\'7\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Remove\' rs:number=\'12\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchStageScore\'\n'
    + '\t\t\t rs:basecolumn=\'RemoveScoring\'>\n'
    + '\t\t\t<s:datatype dt:type=\'boolean\' dt:maxLength=\'2\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'Deduction\' rs:number=\'13\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchStageScore\'\n'
    + '\t\t\t rs:basecolumn=\'Deduction\'>\n'
    + '\t\t\t<s:datatype dt:type=\'boolean\' dt:maxLength=\'2\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'FlagDelete\' rs:number=\'14\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchStageScore\'\n'
    + '\t\t\t rs:basecolumn=\'FlagForDelete\'>\n'
    + '\t\t\t<s:datatype dt:type=\'boolean\' dt:maxLength=\'2\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'IsDisq\' rs:number=\'15\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchStageScore\'\n'
    + '\t\t\t rs:basecolumn=\'Disqualified\'>\n'
    + '\t\t\t<s:datatype dt:type=\'boolean\' dt:maxLength=\'2\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'DedPctg\' rs:number=\'16\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStageScore\' rs:basecolumn=\'DeductionPercent\'>\n'
    + '\t\t\t<s:datatype dt:type=\'ui1\' dt:maxLength=\'1\' rs:precision=\'3\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'ExtraShot\' rs:number=\'17\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStageScore\' rs:basecolumn=\'ExtraShot\'>\n'
    + '\t\t\t<s:datatype dt:type=\'i2\' dt:maxLength=\'2\' rs:precision=\'5\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'OverTime\' rs:number=\'18\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStageScore\' rs:basecolumn=\'OverTime\'>\n'
    + '\t\t\t<s:datatype dt:type=\'r4\' dt:maxLength=\'4\' rs:precision=\'7\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'HitFactor\' rs:number=\'19\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStageScore\' rs:basecolumn=\'HitFactor\'>\n'
    + '\t\t\t<s:datatype dt:type=\'r4\' dt:maxLength=\'4\' rs:precision=\'7\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'FinalScore\' rs:number=\'20\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStageScore\' rs:basecolumn=\'FinalScore\'>\n'
    + '\t\t\t<s:datatype dt:type=\'i2\' dt:maxLength=\'2\' rs:precision=\'5\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'LastModify\' rs:number=\'21\' rs:nullable=\'true\' rs:maydefer=\'true\' rs:writeunknown=\'true\'\n'
    + '\t\t\t rs:basetable=\'tblMatchStageScore\' rs:basecolumn=\'LastModified\'>\n'
    + '\t\t\t<s:datatype dt:type=\'dateTime\' rs:dbtype=\'variantdate\' dt:maxLength=\'16\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:AttributeType name=\'NoVerify\' rs:number=\'22\' rs:maydefer=\'true\' rs:writeunknown=\'true\' rs:basetable=\'tblMatchStageScore\'\n'
    + '\t\t\t rs:basecolumn=\'NoScoreVerify\'>\n'
    + '\t\t\t<s:datatype dt:type=\'boolean\' dt:maxLength=\'2\' rs:fixedlength=\'true\'/>\n'
    + '\t\t</s:AttributeType>\n'
    + '\t\t<s:extends type=\'rs:rowbase\'/>\n'
    + '\t</s:ElementType>\n'
    + '</s:Schema>\n'
    + '<rs:data>\n'}${
    scores.map((scoresForStage, stageIdx) => scoresForStage.map((score) => {
      if (score.dq) return '';
      const memberId = shooters.findIndex((s) => s.id === score.shooterId) + 1;
      const shooter = shooters.find((s) => s.id === score.shooterId);
      if (!shooter) return '';
      if (shooter.cancelledParticipation) return '';
      const {
        alphas, charlies, deltas, misses, factorStr, total, noShoots, penalties,
      } = summarizeScore({
        score, stage: stages[stageIdx], powerFactor: shooter['power-factor'],
      });

      return ''
        + `\t<z:row MatchId='1' StageId='${stageIdx + 1}' MemberId='${memberId}' ScoreA='${alphas}' ScoreB='0' ScoreC='${charlies}' ScoreD='${deltas}' Misses='${misses}' Penalties='${noShoots || 0}'\n`
        + `\t\t ProcError='${penalties || 0}' ShootTime='${score.time}' Remove='False' Deduction='False' FlagDelete='False' IsDisq='False' DedPctg='0' ExtraShot='0'\n`
        + `\t\t OverTime='0' HitFactor='${factorStr}' FinalScore='${total}' LastModify='${new Date(score.timestamp).toISOString().replace(/\..*$/, '')}' NoVerify='FALSE'/>\n`;
    }).join('')).join('')
  }</rs:data>\n`
    + '</xml>\n';
}

function getPrepareBatch() {
  return ''
    + '@echo off\n'
    + 'dir /s /b /a-d *.XML >files.txt\n'
    + 'makecab /d "CabinetName1=WinMSS.cab" /f files.txt\n'
    + 'del /q /f files.txt\n';
}

export default function WinMSS() {
  const { t } = useTranslation();
  const { match } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const promises = await Promise.all([
          getShooters(match.id),
          getScores(match.id),
          getMatchStaff(match.id),
        ]);

        const allShooters = promises[0].data || [];
        const scoresData = promises[1].data;
        const rawScores = Array.isArray(scoresData) ? scoresData : scoresData?.scores || [];
        const staff = Array.isArray(promises[2].data) ? promises[2].data : [];

        const shootersPcc = allShooters
          .filter((s) => s.division === 'pcc-optics' || s.division === 'pcc-iron')
          .sort((a, b) => a.created - b.created);
        const shootersNoPcc = allShooters
          .filter((s) => s.division !== 'pcc-optics' && s.division !== 'pcc-iron')
          .sort((a, b) => a.created - b.created);

        shootersPcc.forEach((s) => {
          if (parseInt(s.publicId, 10) > 50000) s.publicId = String(1000 + parseInt(Math.random() * 1000, 10));
        });
        shootersNoPcc.forEach((s) => {
          if (parseInt(s.publicId, 10) > 50000) s.publicId = String(1000 + parseInt(Math.random() * 1000, 10));
        });

        let stages = [];
        if (match.stages?.length) {
          const stagesRes = await getStages(match.stages.join(','));
          stages = stagesRes.data || [];
        }

        const latestScoresPerStage = getLatestScoreForShooterPerStage({ scores: rawScores, numStages: stages.length });

        const clubsPcc = uniq(shootersPcc.map((s) => s.club));
        const clubsNoPcc = uniq(shootersNoPcc.map((s) => s.club));

        setData({
          match, shootersPcc, shootersNoPcc, staff, stages, clubsPcc, clubsNoPcc, scores: latestScoresPerStage,
        });
      } catch (err) {
        console.error('Failed to load WinMSS data', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [match.id]);

  const saveFiles = async ({ isPcc }) => {
    const shooters = isPcc ? data.shootersPcc : data.shootersNoPcc;
    const clubs = isPcc ? data.clubsPcc : data.clubsNoPcc;

    saveData(getPrepareBatch(), 'PREPARE.BAT');
    await new Promise((res) => setTimeout(res, 1000));
    saveData(getScoreData({ shooters, scores: data.scores, stages: data.stages }), 'SCORE.XML');
    await new Promise((res) => setTimeout(res, 1000));
    saveData(getClassifyData(), 'CLASSIFY.XML');
    await new Promise((res) => setTimeout(res, 1000));
    saveData(getClubsData({ clubs }), 'CLUB.XML');
    await new Promise((res) => setTimeout(res, 1000));
    saveData(getEnrolledData({ shooters, scores: data.scores, match: data.match }), 'ENROLLED.XML');
    await new Promise((res) => setTimeout(res, 1000));
    saveData(getMemberData({ shooters }), 'MEMBER.XML');
    await new Promise((res) => setTimeout(res, 1000));
    saveData(getMatchData({ match: data.match, staff: data.staff, isPcc }), 'THEMATCH.XML');
    await new Promise((res) => setTimeout(res, 1000));
    saveData(getSquadData({ match: data.match }), 'SQUAD.XML');
    await new Promise((res) => setTimeout(res, 1000));
    saveData(getStagesData({ stages: data.stages }), 'STAGE.XML');
    await new Promise((res) => setTimeout(res, 1000));
    saveData(getTagData(), 'TAG.XML');
    await new Promise((res) => setTimeout(res, 1000));
    saveData(getTeamData(), 'TEAM.XML');
  };

  if (loading || !data) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
        <CircularProgress size={75} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
      <div style={{ padding: '0px 24px' }}>
        <Typography variant="body2" style={{ fontWeight: 400 }}>{t('winmss.description1')}</Typography>
        <Typography variant="body2" style={{ fontWeight: 400, paddingTop: '12px' }}>{t('winmss.description2')}</Typography>
        <Typography variant="body2" style={{ fontWeight: 400, padding: '0px 12px' }}>{t('winmss.step1')}</Typography>
        <Typography variant="body2" style={{ fontWeight: 400, padding: '0px 12px' }}>{t('winmss.step2')}</Typography>
        <Typography variant="body2" style={{ fontWeight: 400, padding: '0px 12px' }}>{t('winmss.step3')}</Typography>
        <Typography variant="caption" style={{ fontWeight: '500', paddingTop: '12px' }}>{t('winmss.lastnote')}</Typography>
        <Button onClick={() => saveFiles({ isPcc: false })} variant="contained" color="primary" fullWidth style={{ marginTop: '24px' }}>
          {t('winmss.export')}
        </Button>
        {data.shootersPcc.length > 0 && (
          <Button onClick={() => saveFiles({ isPcc: true })} variant="contained" color="primary" fullWidth style={{ marginTop: '24px' }}>
            {t('winmss.exportpcc')}
          </Button>
        )}
      </div>
    </div>
  );
}

// Named exports for testing
export {
  getMemberData,
  getMatchData,
  getSquadData,
  getStagesData,
  getClassifyData,
  getClubsData,
  getEnrolledData,
  getTagData,
  getTeamData,
  getScoreData,
  getPrepareBatch,
  publicIdToRegion,
  countryCodeToRegion,
  xmlEscape,
};
